import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase-auth';
import { useAuth } from '../../../contexts/AuthContextLazy';
import { showError } from '../../../utils/toast';

interface Feature {
  id: string;
  feature_key: string;
  display_name: string;
  description: string;
  category: string;
  is_enabled_globally: boolean;
  requires_integration?: string;
  dependencies?: string[];
  is_client_facing: boolean;
  tier_availability?: string[];
}


interface Organization {
  id: string;
  name: string;
}

const FeatureAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [features, setFeatures] = useState<Feature[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState<string>('global');
  const [savingFeature, setSavingFeature] = useState<string | null>(null);

  // Check if user is super admin
  useEffect(() => {
    if (user && userProfile?.role !== 'super_admin') {
      navigate('/portal');
    }
  }, [user, userProfile, navigate]);

  // Fetch all features
  const fetchFeatures = async () => {
    try {
      const { data, error } = await supabase
        .from('portal_features')
        .select('*')
        .order('category', { ascending: true })
        .order('display_name', { ascending: true });

      if (error) throw error;
      setFeatures(data || []);
    } catch (err) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error fetching features:', err);
        }

      }
    }
  };


  // Fetch all organizations
  const fetchOrganizations = async () => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setOrganizations(data || []);
    } catch (err) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error fetching organizations:', err);
        }

      }
    }
  };

  // Load all data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchFeatures(), fetchOrganizations()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Group features by client-facing status
  const pcmFeatures = features.filter(f => !f.is_client_facing);
  const clientFeatures = features.filter(f => f.is_client_facing);

  // Toggle global feature
  const toggleGlobalFeature = async (feature: Feature) => {
    setSavingFeature(feature.feature_key);
    try {
      const { error } = await supabase
        .from('portal_features')
        .update({ is_enabled_globally: !feature.is_enabled_globally })
        .eq('id', feature.id);

      if (error) throw error;
      
      // Update local state
      setFeatures(prev => 
        prev.map(f => 
          f.id === feature.id 
            ? { ...f, is_enabled_globally: !f.is_enabled_globally }
            : f
        )
      );
    } catch (err) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error toggling feature:', err);
        }

      }
      showError('Failed to update feature');
    } finally {
      setSavingFeature(null);
    }
  };


  const categoryIcons: Record<string, string> = {
    core: '🏠',
    documents: '📄',
    communication: '💬',
    portfolio: '📊',
    integration: '🔗'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Quick Stats */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white portal-content-shadow rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Total Features</h3>
          <p className="text-2xl font-bold text-gray-900">{features.length}</p>
        </div>
        <div className="bg-white portal-content-shadow rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Enabled Features</h3>
          <p className="text-2xl font-bold text-green-600">
            {features.filter(f => f.is_enabled_globally).length}
          </p>
        </div>
        <div className="bg-white portal-content-shadow rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">In Development</h3>
          <p className="text-2xl font-bold text-amber-600">
            {features.filter(f => !f.is_enabled_globally).length}
          </p>
        </div>
      </div>

      {/* Organization Selector */}
      <div className="mb-6">
        <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
          Configure features for:
        </label>
        <select
          id="organization"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-amber-500 focus:border-amber-500"
          value={selectedOrg}
          onChange={(e) => setSelectedOrg(e.target.value)}
        >
          <option value="global">All Organizations (Global Default)</option>
          {organizations.map(org => (
            <option key={org.id} value={org.id}>{org.name}</option>
          ))}
        </select>
      </div>

      {/* Feature Sections */}
      <div className="space-y-8">
        {/* PCM Internal Features */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">PCM Internal Features</h2>
          <div className="space-y-6">
            {Object.entries(
              pcmFeatures.reduce((acc, feature) => {
                if (!acc[feature.category]) acc[feature.category] = [];
                acc[feature.category].push(feature);
                return acc;
              }, {} as Record<string, Feature[]>)
            ).map(([category, categoryFeatures]) => (
              <div key={category} className="bg-white portal-content-shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-base font-medium text-gray-900 flex items-center">
                    <span className="text-xl mr-2">{categoryIcons[category]}</span>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </h3>
                </div>
                <div className="divide-y divide-gray-200">
                  {categoryFeatures.map(feature => (
                    <div key={feature.id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-900">
                            {feature.display_name}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">
                            {feature.description}
                          </p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                            feature.is_enabled_globally 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {feature.is_enabled_globally ? 'Live' : 'In Development'}
                          </span>
                          <button
                            type="button"
                            className={`
                              ${feature.is_enabled_globally ? 'bg-amber-500' : 'bg-gray-200'}
                              relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full
                              transition-colors duration-200 ease-in-out
                              ${savingFeature === feature.feature_key ? 'opacity-50' : ''}
                            `}
                            onClick={() => toggleGlobalFeature(feature)}
                            disabled={savingFeature === feature.feature_key}
                          >
                            <span
                              className={`
                                ${feature.is_enabled_globally ? 'translate-x-5' : 'translate-x-0'}
                                pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow
                                transition duration-200 ease-in-out
                              `}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Client-Facing Features */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Client-Facing Features</h2>
          <div className="space-y-6">
            {Object.entries(
              clientFeatures.reduce((acc, feature) => {
                if (!acc[feature.category]) acc[feature.category] = [];
                acc[feature.category].push(feature);
                return acc;
              }, {} as Record<string, Feature[]>)
            ).map(([category, categoryFeatures]) => (
              <div key={category} className="bg-white portal-content-shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-base font-medium text-gray-900 flex items-center">
                    <span className="text-xl mr-2">{categoryIcons[category]}</span>
                    {category.charAt(0).toUpperCase() + category.slice(1)} (Client)
                  </h3>
                </div>
                <div className="divide-y divide-gray-200">
                  {categoryFeatures.map(feature => (
                    <div key={feature.id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-900">
                            {feature.display_name}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">
                            {feature.description}
                          </p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                            feature.is_enabled_globally 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {feature.is_enabled_globally ? 'Live' : 'In Development'}
                          </span>
                          <button
                            type="button"
                            className={`
                              ${feature.is_enabled_globally ? 'bg-amber-500' : 'bg-gray-200'}
                              relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full
                              transition-colors duration-200 ease-in-out
                              ${savingFeature === feature.feature_key ? 'opacity-50' : ''}
                            `}
                            onClick={() => toggleGlobalFeature(feature)}
                            disabled={savingFeature === feature.feature_key}
                          >
                            <span
                              className={`
                                ${feature.is_enabled_globally ? 'translate-x-5' : 'translate-x-0'}
                                pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow
                                transition duration-200 ease-in-out
                              `}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureAdminPage;